import { useState, useEffect, useMemo } from 'react';
import {
  Popover,
  Box,
  Button,
  Chip,
  TextField,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  Typography,
  Stack,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { useActiveTags } from '../useKronosData';
import type { TimeEntry } from '@altyor-kronos/shared-types';
import type { DesignationOption } from '../designationOptions';
import { encodeExistingOptionId } from '../designationOptions';
import type { TaskSuggestion } from '../taskSuggestions';
import { colors } from '../../../core/theme/theme';
import { formatDurationSeconds } from '../timeEntries';
import { formatTimeFromMinutes } from './calendarLayout';

export interface PopoverProjectOption {
  id: string;
  code: string;
  name: string;
}

export interface CalendarEntryPopoverProps {
  open: boolean;
  anchorPosition?: { top: number; left: number };
  onClose: () => void;
  /** Pre-filled start/end in minutes since midnight. */
  startMinute: number;
  endMinute: number;
  /** Date of the entry (for display and Firestore write). */
  date: Date;
  /** If editing, the existing entry data. Null for create. */
  existingEntry?: { id: string; data: TimeEntry } | null;
  projects: PopoverProjectOption[];
  designations: DesignationOption[];
  /**
   * Cross-project "recently used" designations, shown while no project
   * is selected. Picking one selects its project too.
   */
  recentOptions?: DesignationOption[];
  /** Description suggestions, pre-scoped to the selected project by the parent. */
  taskSuggestions?: TaskSuggestion[];
  onProjectChange: (projectId: string) => void;
  onSave: (payload: {
    projectId: string;
    designationId: string;
    description: string;
    startMinute: number;
    endMinute: number;
    date: Date;
    tags: string[];
    billable: boolean;
  }) => Promise<void>;
  onDelete?: (entryId: string) => Promise<void>;
}

export default function CalendarEntryPopover({
  open,
  anchorPosition,
  onClose,
  startMinute,
  endMinute,
  date,
  existingEntry,
  projects,
  designations,
  recentOptions = [],
  taskSuggestions = [],
  onProjectChange,
  onSave,
  onDelete,
}: CalendarEntryPopoverProps) {
  const { t } = useTranslation();
  const isEdit = Boolean(existingEntry);
  const tagOptions = useActiveTags();

  const [projectId, setProjectId] = useState('');
  const [designationId, setDesignationId] = useState('');
  const [description, setDescription] = useState('');
  const [start, setStart] = useState(startMinute);
  const [end, setEnd] = useState(endMinute);
  const [tags, setTags] = useState<string[]>([]);
  const [billable, setBillable] = useState(false);
  const [saving, setSaving] = useState(false);

  // Reset form when popover opens with new data
  useEffect(() => {
    if (!open) return;
    if (existingEntry) {
      const e = existingEntry.data;
      setProjectId(e.projectId);
      // Encode to match the DesignationOption.id format used by buildDesignationOptions
      setDesignationId(encodeExistingOptionId(e.designationId));
      setDescription(e.description ?? '');
      const st = e.startTime.toDate();
      setStart(st.getHours() * 60 + st.getMinutes());
      const et = e.endTime.toDate();
      setEnd(et.getHours() * 60 + et.getMinutes());
      setTags(e.tags ?? []);
      setBillable(e.billable ?? false);
      onProjectChange(e.projectId);
    } else {
      setProjectId('');
      setDesignationId('');
      setDescription('');
      setStart(startMinute);
      setEnd(endMinute);
      setTags([]);
      setBillable(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, existingEntry?.id, startMinute, endMinute]);

  const durationSeconds = Math.max(0, (end - start) * 60);
  const canSave = projectId && designationId && end > start;

  const selectedProject = projects.find((p) => p.id === projectId) ?? null;
  // The value may come from the cross-project recent list, which the
  // project's own designation list replaces once loaded — look in both.
  const selectedDesignation =
    designations.find((d) => d.id === designationId) ??
    recentOptions.find((d) => d.id === designationId) ??
    null;

  // No project yet → offer the recent (project, designation) pairs.
  // The selected option is kept in the list while the project's own
  // designations load, so the field never blanks out mid-pick.
  const designationOptions = useMemo(() => {
    const base = projectId ? designations : recentOptions;
    if (selectedDesignation && !base.some((o) => o.id === selectedDesignation.id)) {
      return [selectedDesignation, ...base];
    }
    return base;
  }, [projectId, designations, recentOptions, selectedDesignation]);

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await onSave({
        projectId,
        designationId,
        description,
        startMinute: start,
        endMinute: end,
        date,
        tags,
        billable,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!existingEntry || !onDelete) return;
    setSaving(true);
    try {
      await onDelete(existingEntry.id);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover
      open={open}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition}
      onClose={onClose}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{ paper: { sx: { width: 360, p: 2 } } }}
    >
      <Stack spacing={1.5}>
        {/* Project */}
        <Autocomplete
          size="small"
          options={projects}
          value={selectedProject}
          onChange={(_, v) => {
            const id = v?.id ?? '';
            setProjectId(id);
            setDesignationId('');
            onProjectChange(id);
          }}
          getOptionLabel={(o) => `${o.code} — ${o.name}`}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          renderInput={(params) => (
            <TextField {...params} label={t('kronos.calendar.project')} />
          )}
        />

        {/* Designation */}
        <Autocomplete
          size="small"
          options={designationOptions}
          value={selectedDesignation}
          onChange={(_, v) => {
            setDesignationId(v?.id ?? '');
            // A cross-project recent carries its project — select it too.
            if (v?.projectId && v.projectId !== projectId) {
              setProjectId(v.projectId);
              onProjectChange(v.projectId);
            }
          }}
          getOptionLabel={(o) => o.label}
          groupBy={(o) => o.groupLabel}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          renderOption={(optionProps, o) => (
            <Box
              component="li"
              {...optionProps}
              key={o.id}
              sx={{ justifyContent: 'space-between !important', gap: 1 }}
            >
              <Typography variant="body2" component="span">
                {o.label}
              </Typography>
              {o.recent && (
                <Chip
                  label={t('kronos.tracker.designationRecentBadge')}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '0.65rem',
                    bgcolor: colors.yellow,
                    color: '#000',
                    '& .MuiChip-label': { px: 0.75 },
                  }}
                />
              )}
            </Box>
          )}
          renderInput={(params) => (
            <TextField {...params} label={t('kronos.calendar.designation')} />
          )}
        />

        {/* Description — freeSolo autocomplete over the user's past tasks
            on this project; picking one also pre-fills the designation. */}
        <Autocomplete<TaskSuggestion, false, false, true>
          freeSolo
          size="small"
          options={taskSuggestions}
          inputValue={description}
          onInputChange={(_, v) => setDescription(v)}
          filterOptions={(opts, state) => {
            const needle = state.inputValue.trim().toLowerCase();
            if (!needle) return opts;
            return opts.filter((o) => o.label.toLowerCase().includes(needle));
          }}
          getOptionLabel={(o) => (typeof o === 'string' ? o : o.label)}
          isOptionEqualToValue={(o, v) =>
            typeof o !== 'string' &&
            typeof v !== 'string' &&
            o.projectId === v.projectId &&
            o.label === v.label &&
            o.designationId === v.designationId
          }
          onChange={(_, value) => {
            if (!value || typeof value === 'string') return;
            setDescription(value.label);
            setDesignationId(encodeExistingOptionId(value.designationId));
            if (value.projectId !== projectId) {
              setProjectId(value.projectId);
              onProjectChange(value.projectId);
            }
            // ponytail: the dragged slot wins over the suggestion's duration —
            // in the calendar the user drew the time on purpose.
          }}
          renderOption={(optionProps, o) => (
            <Box
              component="li"
              {...optionProps}
              key={`${o.projectId}|${o.label}|${o.designationId}`}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="body2">{o.label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {o.projectLabel} · {o.designationLabel} ·{' '}
                  {t('kronos.tracker.suggestionUsedCount', { count: o.count })}
                </Typography>
              </Box>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('kronos.calendar.description')}
              multiline
              rows={2}
            />
          )}
        />

        {/* Time range */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            size="small"
            type="time"
            label={t('kronos.calendar.startTime')}
            value={formatTimeFromMinutes(start)}
            onChange={(e) => {
              const [h, m] = e.target.value.split(':').map(Number);
              if (!isNaN(h) && !isNaN(m)) setStart(h * 60 + m);
            }}
            sx={{ flex: 1 }}
            slotProps={{ htmlInput: { step: 900 } }}
          />
          <Typography variant="body2" color="text.secondary">
            →
          </Typography>
          <TextField
            size="small"
            type="time"
            label={t('kronos.calendar.endTime')}
            value={formatTimeFromMinutes(end)}
            onChange={(e) => {
              const [h, m] = e.target.value.split(':').map(Number);
              if (!isNaN(h) && !isNaN(m)) setEnd(h * 60 + m);
            }}
            sx={{ flex: 1 }}
            slotProps={{ htmlInput: { step: 900 } }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 50 }}>
            {t('kronos.calendar.duration')}: {formatDurationSeconds(durationSeconds)}
          </Typography>
        </Box>

        {/* Tags + Billable */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Autocomplete
            size="small"
            multiple
            options={[...new Set([...tagOptions, ...tags])]}
            value={tags}
            onChange={(_, v) => setTags(v)}
            renderInput={(params) => (
              <TextField {...params} label={t('kronos.calendar.tags')} />
            )}
            sx={{ flex: 1 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={billable}
                onChange={(e) => setBillable(e.target.checked)}
                size="small"
              />
            }
            label={t('kronos.calendar.billable')}
          />
        </Box>

        {/* Actions */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1 }}>
          {isEdit && onDelete ? (
            <Button
              color="error"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
              disabled={saving}
            >
              {t('kronos.calendar.delete')}
            </Button>
          ) : (
            <Box />
          )}
          <Button
            variant="contained"
            size="small"
            onClick={handleSave}
            disabled={!canSave || saving}
          >
            {t('kronos.calendar.save')}
          </Button>
        </Box>
      </Stack>
    </Popover>
  );
}
